import { CircularProgress, Grid, Button, FormControl, InputLabel, Select, MenuItem, Tabs, Tab, Box } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ResponsiveSankey } from '@nivo/sankey';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Checkbox, ListItemText, OutlinedInput } from '@mui/material';
import { Lightbulb } from '@mui/icons-material';
import dayjs from 'dayjs';
import { getAllFacilities, getAllProject, getWaterBalanceData } from '../../Redux/actions';
import InsightsModal from '../Common/InsightsModal';
import { formatDate, generateInsightKey } from '../../utils';
import WaterUsageGauges from './WaterUsageGauges';

// Date filter options for digitalisation view
const DATE_FILTER_OPTIONS = [
    { label: 'Today', value: 'today', days: 0 },
    { label: 'Previous Day', value: 'previous', days: 1 },
    { label: '7 - Days', value: '7days', days: 7 },
    { label: '30 - Days', value: '30days', days: 30 },
    { label: '90 - Days', value: '90days', days: 90 },
];

const CustomTooltip = (params) => {
    const { link, node } = params;
    if (link) {
        // Use full original labels from source/target nodes
        const sourceLabel = link.source?.data?.originalLabel || link.source?.label;
        const targetLabel = link.target?.data?.originalLabel || link.target?.label;
        
        // For energy/OCEMS links, don't show value since it's already in the target label
        const isEnergyLink = sourceLabel && (
            sourceLabel.startsWith('STP Energy') || 
            sourceLabel.startsWith('WTP Energy') || 
            sourceLabel.startsWith('ETP Energy')
        );
        const isOcemsLink = sourceLabel && (
            sourceLabel === 'pH' || 
            sourceLabel === 'TSS' || 
            sourceLabel === 'BOD' || 
            sourceLabel === 'COD'
        );
        const isEquipmentLink = (link.source?.id && link.source.id.startsWith('equip_')) ||
            (sourceLabel && /running\s*time/i.test(sourceLabel));

        const cleanEquipLabel = (label) =>
            (label || '').replace(/\s*,?\s*running\s*time\s*,?\s*hrs?\s*/i, '').trim();

        const linkLabel = isEquipmentLink
            ? (() => {
                const meterName = cleanEquipLabel(sourceLabel);
                const valueMatch = (targetLabel || '').match(/[\d.]+\s*Hrs?/i);
                return `${meterName} → ${valueMatch ? valueMatch[0] : link.value}`;
            })()
            : `${sourceLabel} → ${targetLabel}${(isEnergyLink || isOcemsLink) ? '' : ` : ${link.value}`}`;

        return (<div
            style={{
                padding: '6px 10px',
                background: 'white',
                fontSize: '12px',
                border: '1px solid #ccc',
                minWidth: '200px',
                maxWidth: '400px',
                whiteSpace: 'normal',
                wordWrap: 'break-word'
            }}
        >
            <strong>{linkLabel}</strong>
        </div>)
    } else {
        // Use full original label if available
        const fullLabel = node?.data?.originalLabel || node?.label;
        
        // Don't show value for energy/OCEMS nodes (they use fixed value for consistent height)
        const isEnergyNode = fullLabel && (
            fullLabel.startsWith('STP Energy') || 
            fullLabel.startsWith('WTP Energy') || 
            fullLabel.startsWith('ETP Energy')
        );
        const isOcemsNode = fullLabel && (
            fullLabel === 'pH' || fullLabel === 'TSS' || 
            fullLabel === 'BOD' || fullLabel === 'COD' ||
            fullLabel.startsWith('pH -') || fullLabel.startsWith('TSS -') || 
            fullLabel.startsWith('BOD -') || fullLabel.startsWith('COD -')
        );
        const isEquipmentNode = fullLabel && (
            fullLabel.startsWith('equip_') || /- [\d.]+\s*Hrs?$/i.test(fullLabel)
        );
        
        let valueLabel = '';
        if (!isEnergyNode && !isOcemsNode && !isEquipmentNode && node.label.indexOf('(') !== -1) {
            valueLabel = `(${node.value})`;
        }
        
        return <div
            style={{
                padding: '6px 10px',
                background: 'white',
                fontSize: '12px',
                border: '1px solid #ccc',
                maxWidth: '400px',
                whiteSpace: 'normal',
                wordWrap: 'break-word'
            }}
        >
            <strong>{fullLabel} {valueLabel}</strong>
        </div>
    }
}

// Legend component for water and energy categories
const ChartLegend = ({ showEquipment = true }) => {
    const waterLegendItems = [
        { label: 'Fresh Water', color: 'green' },
        { label: 'Recycled Water', color: '#ffa600' },
        { label: 'Effluent Water (ETP)', color: '#e377c2' },
        { label: 'RO System', color: '#17becf' },
        { label: 'Reuse Opportunity', color: '#1565c0' }
    ];

    const energyLegendItems = [
        { label: 'STP Energy', color: '#8B5CF6' },
        { label: 'WTP Energy', color: '#4F46E5' },
        { label: 'ETP Energy', color: '#9b24d6' }
    ];

    const ocemsLegendItems = [
        { label: 'pH', color: '#2196F3' },
        { label: 'TSS', color: '#FF9800' },
        { label: 'BOD', color: '#4CAF50' },
        { label: 'COD', color: '#9C27B0' }
    ];

    const equipmentLegendItems = [
        { label: 'Equipment Running Hours', color: '#4CAF50' }
    ];

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '8px 12px',
            backgroundColor: 'white',
            borderRadius: '6px',
            border: '1px solid #e0e0e0',
            fontSize: '12px',
            // marginLeft: '16px',
            width: 'fit-content'
        }}>
            {/* Water Legend */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#333', minWidth: '50px' }}>Water:</span>
                {waterLegendItems.map((item, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '14px',
                            height: '14px',
                            backgroundColor: item.color,
                            borderRadius: '2px'
                        }} />
                        <span style={{ fontSize: '11px', color: '#555', whiteSpace: 'nowrap' }}>{item.label}</span>
                    </div>
                ))}
            </div>
            {/* Energy Legend */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#333', minWidth: '50px' }}>Energy:</span>
                {energyLegendItems.map((item, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '14px',
                            height: '14px',
                            backgroundColor: item.color,
                            borderRadius: '2px'
                        }} />
                        <span style={{ fontSize: '11px', color: '#555', whiteSpace: 'nowrap' }}>{item.label}</span>
                    </div>
                ))}
            </div>
            {/* OCEMS (RTM) Legend */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#333', minWidth: '50px' }}>OCEMS (RTM):</span>
                {ocemsLegendItems.map((item, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '14px',
                            height: '14px',
                            backgroundColor: item.color,
                            borderRadius: '2px'
                        }} />
                        <span style={{ fontSize: '11px', color: '#555', whiteSpace: 'nowrap' }}>{item.label}</span>
                    </div>
                ))}
            </div>
            {/* Equipment Running Hours Legend */}
            {showEquipment && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#333', minWidth: '50px' }}>Equipment:</span>
                    {equipmentLegendItems.map((item, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{
                                width: '14px',
                                height: '14px',
                                backgroundColor: item.color,
                                borderRadius: '2px'
                            }} />
                            <span style={{ fontSize: '11px', color: '#555', whiteSpace: 'nowrap' }}>{item.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// Summary card component for water management KPIs
const SummaryCard = ({ label, value, unit, subtitle, borderColor = '#2e7d32', valueColor = '#1a1a1a' }) => (
    <div style={{
        flex: '1 1 0',
        minWidth: '140px',
        padding: '12px 16px',
        backgroundColor: '#fff',
        borderRadius: '6px',
        border: '1px solid #e0e0e0',
        borderTop: `4px solid ${borderColor}`,
        textAlign: 'left'
    }}>
        <div style={{ fontSize: '11px', color: '#666', fontWeight: 500, marginBottom: '4px', textTransform: 'uppercase' }}>{label}</div>
        <div style={{ fontSize: '22px', fontWeight: 700, color: valueColor, marginBottom: subtitle ? '4px' : '0' }}>
            {value} <span style={{ fontSize: '13px', fontWeight: 400, color: '#888' }}>{unit}</span>
        </div>
        {subtitle && (
            <div style={{ fontSize: '11px', color: '#999', fontWeight: 400, marginTop: '4px' }}>{subtitle}</div>
        )}
    </div>
);

// Fresh Water summary cards
const FreshWaterSummaryCards = ({ summary }) => {
    if (!summary?.wtp) return null;
    const wtp = summary.wtp;
    const meteredSource = wtp.source || 0;
    const treatment = wtp.treatment || 0;
    const consumption = wtp.consumption || 0;
    // Unmetered inlet = water flowing into treatment that wasn't captured by source meters
    const unmeteredSource = Math.max(0, treatment - meteredSource);
    const totalSource = meteredSource + unmeteredSource; // = max(source, treatment)
    const lossBackwash = Math.max(0, meteredSource - treatment);
    const treatmentEfficiency = totalSource > 0
        ? Math.min(100, parseFloat(((treatment / totalSource) * 100).toFixed(1)))
        : 0;
    const treatmentMinusConsumption = treatment - consumption;
    const sourceMinusConsumption = meteredSource - consumption;
    const netSupplyBalance = treatmentMinusConsumption >= 0
        ? treatmentMinusConsumption
        : sourceMinusConsumption >= 0
            ? sourceMinusConsumption
            : null;

    return (
        <div style={{ marginBottom: '16px' }}>
            {/* Row 1: Source, Treatment, Consumption */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <SummaryCard
                    label="Source — Metered"
                    value={Math.round(meteredSource).toLocaleString()}
                    unit="KL"
                    borderColor="#2e7d32"
                    valueColor="#1a1a1a"
                />
                <SummaryCard
                    label="Source — Unmetered"
                    value={unmeteredSource > 0 ? Math.round(unmeteredSource).toLocaleString() : '—'}
                    unit={unmeteredSource > 0 ? 'KL' : ''}
                    borderColor="#d32f2f"
                    valueColor="#d32f2f"
                />
                <SummaryCard
                    label="Total Source"
                    value={Math.round(totalSource).toLocaleString()}
                    unit="KL"
                    borderColor="#e65100"
                    valueColor="#e65100"
                />
                <SummaryCard
                    label="Treatment"
                    value={Math.round(treatment).toLocaleString()}
                    unit="KL"
                    borderColor="#1565c0"
                    valueColor="#1565c0"
                />
                <SummaryCard
                    label="Consumption"
                    value={Math.round(consumption).toLocaleString()}
                    unit="KL"
                    borderColor="#00695c"
                    valueColor="#00695c"
                />
            </div>
            {/* Row 2: Loss, Efficiency, Balance */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <SummaryCard
                    label="Loss / Backwash"
                    value={lossBackwash > 0 ? Math.round(lossBackwash).toLocaleString() : '—'}
                    unit={lossBackwash > 0 ? 'KL' : ''}
                    borderColor="#2e7d32"
                    valueColor="#1a1a1a"
                />
                <SummaryCard
                    label="Treatment Efficiency"
                    value={`${treatmentEfficiency}%`}
                    unit=""
                    subtitle="(FW Treatment ÷ Source) × 100"
                    borderColor="#6a1b9a"
                    valueColor="#6a1b9a"
                />
                <SummaryCard
                    label="Net Supply Balance"
                    value={netSupplyBalance === null ? '-' : Math.round(netSupplyBalance).toLocaleString()}
                    unit={netSupplyBalance === null ? '' : 'KL'}
                    subtitle={
                        netSupplyBalance === null ? '' :
                        treatmentMinusConsumption >= 0 ? 'Treatment – Consumption' : 'Source – Consumption'
                    }
                    borderColor="#e65100"
                    valueColor="#e65100"
                />
            </div>
        </div>
    );
};

// Recycle Water summary cards + OEE KPIs
const RecycleWaterSummaryCards = ({ summary, oee }) => {
    if (!summary?.stp) return null;
    const stp = summary.stp;
    const breakdown = stp.consumptionBreakdown || {};

    const garden = Math.round(breakdown.garden || breakdown.Garden || 0);
    const flushing = Math.round(breakdown.flushing || breakdown.Flushing || 0);
    const cooling = Math.round(breakdown.cooling || breakdown.cooling_tower || breakdown.Cooling || 0);
    const reuse = garden + flushing + cooling;
    const reuseOpportunity = Math.max(0, Math.round(stp.treatment || 0) - reuse);

    return (
        <div style={{ marginBottom: '16px' }}>
            {/* Row 1: Water flow cards */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <SummaryCard
                    label="RW Source"
                    value={Math.round(stp.source || 0).toLocaleString()}
                    unit="KL"
                    borderColor="#2e7d32"
                    valueColor="#2e7d32"
                />
                <SummaryCard
                    label="RW Treated"
                    value={Math.round(stp.treatment || 0).toLocaleString()}
                    unit="KL"
                    borderColor="#2e7d32"
                    valueColor="#2e7d32"
                />
                <SummaryCard
                    label="Reuse"
                    value={reuse.toLocaleString()}
                    unit="KL"
                    subtitle="Flushing + Cooling Tower + Garden"
                    borderColor="#1565c0"
                    valueColor="#1565c0"
                />
                <SummaryCard
                    label="Reuse Opportunity"
                    value={reuseOpportunity.toLocaleString()}
                    unit="KL"
                    subtitle="Treated – Reuse"
                    borderColor="#e65100"
                    valueColor="#e65100"
                />
                <SummaryCard
                    label="No. of STPs"
                    value={oee?.num_stps ?? '—'}
                    unit=""
                    subtitle="STP inlets with flow > 0"
                    borderColor="#6a1b9a"
                    valueColor="#6a1b9a"
                />
            </div>
            {/* Row 2: OEE KPI cards */}
            {oee && (
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <SummaryCard
                        label="KW / KL"
                        value={oee.kw_per_kl}
                        unit=""
                        subtitle="STP Energy kWh ÷ RW Treated KL"
                        borderColor="#d32f2f"
                        valueColor="#6a1b9a"
                    />
                    <SummaryCard
                        label="OEE Index"
                        value={`${oee.oee_index}%`}
                        unit=""
                        subtitle="Availability × Performance × Quality"
                        borderColor="#6a1b9a"
                        valueColor="#d32f2f"
                    />
                    <SummaryCard
                        label="Availability"
                        value={`${oee.availability}%`}
                        unit=""
                        subtitle="Equipment running hrs (backend calc)"
                        borderColor="#e65100"
                        valueColor="#e65100"
                    />
                    <SummaryCard
                        label="Performance"
                        value={`${oee.performance}%`}
                        unit=""
                        subtitle="RW Treated ÷ RW Source"
                        borderColor="#1565c0"
                        valueColor="#1565c0"
                    />
                    <SummaryCard
                        label="Quality"
                        value={`${oee.quality}%`}
                        unit=""
                        subtitle="Parameters Within Limits ÷ Total Monitored"
                        borderColor="#2e7d32"
                        valueColor="#2e7d32"
                    />
                </div>
            )}
        </div>
    );
};

export default function WaterBalanceSankey({ clientId, projectIds = null, showFilters = true, showEquipment = true }) {
    const dispatch = useDispatch();
    const currentUserRole = useSelector(st => st.role?.data?.data);
    const isAdmin = currentUserRole?.some(r => (r.role === 'admin' || r.role === 'spoc') && r.client_id === null) ?? false;
    const [sankeyData, setSankeyData] = useState(null);
    const [sankeyLoader, setSankeyLoader] = useState(false);
    const [selectedFacilities, setSelectedFacilities] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [projects, setProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
    const maxDate = dayjs().subtract(1, 'days').toDate();
    const [fromDate, setFromDate] = useState(dayjs().subtract(30, 'days').toDate());
    const [toDate, setToDate] = useState(dayjs().subtract(1, 'days').toDate());
    const [selectedDateFilter, setSelectedDateFilter] = useState('30days'); // Default to 30 days for digitalisation view

    const [summaryData, setSummaryData] = useState(null);
    const [oeeData, setOeeData] = useState(null);
    const [activeTab, setActiveTab] = useState(0); // 0 = Fresh Water, 1 = Recycle Water

    const [openInsightModal, setOpenInsightModal] = useState(false);
    const [showInsightButton, setShowInsightButton] = useState(true);
    const [insightKey, setInsightKey] = useState(null);

    // Create stable reference for projectIds to prevent infinite loop
    const projectIdsKey = useMemo(() => {
        return projectIds ? JSON.stringify(projectIds) : null;
    }, [projectIds]);

    // Split sankeyData into separate category data (fresh_water, recycled_water, etp, ro, energy, ocems)
    const { freshWaterData, recycledWaterData, etpData, roData, energySankeyData, levelData, ocemsData, equipmentData } = useMemo(() => {
        if (!sankeyData || !sankeyData.nodes || !sankeyData.links) {
            return { 
                freshWaterData: null, 
                recycledWaterData: null, 
                etpData: null, 
                roData: null, 
                energySankeyData: null,
                levelData: null,
                ocemsData: null,
                equipmentData: null
            };
        }

        // Helper function to extract data for a specific category
        const extractCategoryData = (category) => {
            const categoryNodes = sankeyData.nodes.filter(node => node.category === category);
            if (categoryNodes.length === 0) return null;

            const categoryNodeIds = new Set(categoryNodes.map(node => node.id));
            const categoryLinks = sankeyData.links.filter(link => 
                categoryNodeIds.has(link.source) || categoryNodeIds.has(link.target)
            );

            // Include any connected nodes that may have different categories (like unmetered nodes)
            categoryLinks.forEach(link => {
                if (!categoryNodeIds.has(link.source)) {
                    const sourceNode = sankeyData.nodes.find(n => n.id === link.source);
                    if (sourceNode && !categoryNodes.find(n => n.id === sourceNode.id)) {
                        categoryNodes.push(sourceNode);
                        categoryNodeIds.add(sourceNode.id);
                    }
                }
                if (!categoryNodeIds.has(link.target)) {
                    const targetNode = sankeyData.nodes.find(n => n.id === link.target);
                    if (targetNode && !categoryNodes.find(n => n.id === targetNode.id)) {
                        categoryNodes.push(targetNode);
                        categoryNodeIds.add(targetNode.id);
                    }
                }
            });

            return categoryNodes.length > 0 ? { nodes: categoryNodes, links: categoryLinks } : null;
        };

        return {
            freshWaterData: extractCategoryData('fresh_water'),
            recycledWaterData: extractCategoryData('recycled_water'),
            etpData: extractCategoryData('etp'),
            roData: extractCategoryData('ro'),
            energySankeyData: extractCategoryData('energy'),
            levelData: extractCategoryData('level'),
            ocemsData: extractCategoryData('ocems'),
            equipmentData: extractCategoryData('equipment')
        };
    }, [sankeyData]);

    // Helper function to get date range based on selected filter
    const getDateRangeFromFilter = (filterValue) => {
        const today = dayjs();
        let from, to;
        
        switch (filterValue) {
            case 'today':
                from = today.startOf('day');
                to = today;
                break;
            case 'previous':
                from = today.subtract(1, 'day').startOf('day');
                to = today.subtract(1, 'day').endOf('day');
                break;
            case '7days':
                from = today.subtract(7, 'days');
                to = today.subtract(1, 'day');
                break;
            case '30days':
                from = today.subtract(30, 'days');
                to = today.subtract(1, 'day');
                break;
            case '90days':
                from = today.subtract(90, 'days');
                to = today.subtract(1, 'day');
                break;
            default:
                from = today.subtract(30, 'days');
                to = today.subtract(1, 'day');
        }
        
        return { from: from.toDate(), to: to.toDate() };
    };

    // Handle date filter button click (for digitalisation view)
    const handleDateFilterChange = (filterValue) => {
        setSelectedDateFilter(filterValue);
        const { from, to } = getDateRangeFromFilter(filterValue);
        setFromDate(from);
        setToDate(to);

        // Fetch data with new date range
        if (projectIds && projectIds.length > 0) {
            setSankeyLoader(true);
            const params = {
                project_ids: projectIds,
                from_date: dayjs(from).format('YYYY-MM-DD') + ' 00:00:00',
                to_date: dayjs(to).format('YYYY-MM-DD') + ' 23:59:59'
            };

            dispatch(getWaterBalanceData(params)).then((res) => {
                setSankeyLoader(false);
                if (res && res.status === 200 && res.data?.success) {
                    setSankeyData(res.data.data?.chart_data);
                    setSummaryData(res.data.data?.summary || null);
                    setOeeData(res.data.data?.oee || null);
                }
            }).catch(() => {
                setSankeyLoader(false);
            });
        }
    };

    // Get display label for current filter
    const getFilterDisplayLabel = () => {
        const filter = DATE_FILTER_OPTIONS.find(f => f.value === selectedDateFilter);
        return filter ? filter.label : '30 - Days';
    };

    // Prepare insight modal with sankey data
    const prepareInsightModal = () => {
        const formattedFromDate = formatDate(fromDate);
        const formattedToDate = formatDate(toDate);
        // Include projectIds in the key to make it unique for different project combinations
        const projectIdsString = projectIds ? projectIds.sort().join('-') : (selectedProjects.length > 0 ? selectedProjects.sort().join('-') : 'all');
        const key = generateInsightKey(clientId, formattedFromDate, formattedToDate, `water-balance-sankey-${projectIdsString}`);
        setInsightKey(key);
        setOpenInsightModal(true);
        setShowInsightButton(false);
    };

    useEffect(() => {
        if (!clientId) return;

        // If projectIds are provided (Live View mode), fetch data directly without filters
        if (projectIds && projectIds.length > 0) {
            setHasAppliedFilters(true);
            fetchSankeyDataWithParams([], projectIds);
            return;
        }

        // Otherwise, fetch facilities and projects for filters (Dashboard mode)
        if (showFilters) {
            Promise.all([
                dispatch(getAllFacilities(clientId)),
                dispatch(getAllProject(clientId))
            ]).then(([facilitiesRes, projectsRes]) => {
                const facilityList = facilitiesRes?.data?.allData || [];
                const projectList = projectsRes?.data?.allData || [];

                setFacilities(facilityList);
                setProjects(projectList);

                // Auto-select all facilities and projects
                if (facilityList.length > 0 && projectList.length > 0) {
                    const facilityIds = facilityList.map(f => f.id);
                    const projectIdsAll = projectList.map(p => p.pid);

                    setSelectedFacilities(facilityIds);
                    setSelectedProjects(projectIdsAll);
                    setHasAppliedFilters(true);

                    // Trigger fetchSankeyData after both are loaded
                    setTimeout(() => {
                        fetchSankeyDataWithParams(facilityIds, projectIdsAll);
                    }, 100);
                }
            });
        }
    }, [clientId, projectIdsKey, showFilters, dispatch])

    // Filter projects based on selected facilities
    useEffect(() => {
        if (selectedFacilities.length > 0 && projects.length > 0) {
            const filtered = projects.filter(project =>
                selectedFacilities.includes(project.facility_id)
            );
            setFilteredProjects(filtered);
            // Auto-select all filtered projects
            if (filtered.length > 0) {
                const filteredProjectIds = filtered.map(p => p.pid);
                setSelectedProjects(filteredProjectIds);
            } else {
                setSelectedProjects([]);
            }
        } else {
            setFilteredProjects([]);
            setSelectedProjects([]);
        }
    }, [selectedFacilities, projects])

    const fetchSankeyDataWithParams = (facilityIds, projectIds) => {
        setSankeyLoader(true);
        const params = {};
        if (facilityIds && facilityIds.length > 0) params.facility_ids = facilityIds;
        if (projectIds && projectIds.length > 0) params.project_ids = projectIds;
        if (fromDate) params.from_date = dayjs(fromDate).format('YYYY-MM-DD') + ' 00:00:00';
        if (toDate) params.to_date = dayjs(toDate).format('YYYY-MM-DD') + ' 23:59:59';

        dispatch(getWaterBalanceData(params)).then((res) => {
            setSankeyLoader(false);
            if (res && res.status === 200 && res.data?.success) {
                setSankeyData(res.data.data?.chart_data);
                setSummaryData(res.data.data?.summary || null);
                setOeeData(res.data.data?.oee || null);
            } else {
                setSankeyData(res.data.message);
            }
        }).catch(() => {
            setSankeyLoader(false);
        });
    };

    const fetchSankeyData = () => {
        fetchSankeyDataWithParams(selectedFacilities, selectedProjects);
    };

    const handleApplyFilters = () => {
        setHasAppliedFilters(true);
        fetchSankeyData();
    };

    const handleClearFilters = () => {
        setSelectedFacilities([]);
        setSelectedProjects([]);
        const newFromDate = dayjs().subtract(30, 'days').toDate();
        const newToDate = dayjs().subtract(1, 'days').toDate();
        setFromDate(newFromDate);
        setToDate(newToDate);
        setSankeyData(null);
        setSummaryData(null);
        setOeeData(null);
        setHasAppliedFilters(false);
    };

    const handleFilterChange = (event, filterType) => {
        const { target: { value } } = event;

        const stateMapping = {
            facility: {
                allItems: facilities?.map(facility => facility.id),
                selectedItems: selectedFacilities,
                setSelected: setSelectedFacilities,
                allLabel: "Select All"
            },
            project: {
                allItems: filteredProjects?.map(project => project.pid),
                selectedItems: selectedProjects,
                setSelected: setSelectedProjects,
                allLabel: "Select All"
            }
        };

        const { allItems, selectedItems, setSelected, allLabel } = stateMapping[filterType];

        if (value.includes(allLabel)) {
            if (selectedItems.length === allItems.length) {
                setSelected([]);
            } else {
                setSelected(allItems);
            }
        } else {
            const newValue = typeof value === 'string' ? value.split(',') : value;
            const filteredValue = newValue.filter(item => item !== allLabel);
            setSelected(filteredValue);
        }
    };

    // Wrapper style for Live view to remove side padding
    const liveViewWrapperStyle = !showFilters ? {
        marginLeft: '-24px',
        width: 'calc(100% + 48px)'
    } : {};

    return (
        <div style={liveViewWrapperStyle}>
            {!showFilters && (
                <Grid container spacing={2} style={{ marginBottom: 16, marginTop: 24 }}>
                    <Grid item xs={12}>
                        <h5 style={{ color: '#004d4d' }} className="card-title">Water, Energy & OCEMS Mapping</h5>
                        <p style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                            Showing data for {getFilterDisplayLabel()}
                        </p>
                    </Grid>
                </Grid>
            )}
            {showFilters && (
                <Grid container spacing={2} alignItems="center" style={{ marginBottom: 24, marginTop: 24 }}>
                    <Grid item xs={12}>
                        <h5 style={{ color: '#004d4d' }} className="card-title">Water, Energy & OCEMS Mapping</h5>
                        <p style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                            Showing: {selectedFacilities.length === facilities.length || selectedFacilities.length === 0 ? 'All Facilities' : `${selectedFacilities.length} Facilities`}
                            {' | '}
                            {selectedProjects.length === filteredProjects.length || selectedProjects.length === 0 ? 'All Projects' : `${selectedProjects.length} Projects`}
                        </p>
                    </Grid>
                    <Grid item xs={12} md={2.5}>
                        <FormControl size='small' fullWidth>
                            <InputLabel id="facility-select-label" sx={{ '&.Mui-focused': { color: '#008080' } }}>Facility*</InputLabel>
                            <Select
                                labelId="facility-select-label"
                                multiple
                                value={selectedFacilities}
                                onChange={(e) => handleFilterChange(e, 'facility')}
                                input={<OutlinedInput label="Facility" />}
                                renderValue={(selected) => selected.length === facilities?.length ? 'All Facilities' : selected.length + ' selected'}
                                sx={{
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#008080'
                                    }
                                }}
                                MenuProps={{
                                    autoFocus: false,
                                    disableScrollLock: true,
                                    PaperProps: {
                                        style: {
                                            maxHeight: 350,
                                            overflowY: "auto"
                                        }
                                    }
                                }}
                            >
                                {facilities?.length > 0 && (
                                    <MenuItem value="Select All">
                                        <Checkbox
                                            checked={selectedFacilities?.length === facilities?.length}
                                            indeterminate={selectedFacilities?.length > 0 && selectedFacilities?.length < facilities?.length}
                                            sx={{ '&.Mui-checked': { color: '#008080' }, '&.MuiCheckbox-indeterminate': { color: '#008080' } }}
                                        />
                                        <ListItemText primary="Select All" />
                                    </MenuItem>
                                )}
                                {facilities.map((facility) => (
                                    <MenuItem key={facility.id} value={facility.id}>
                                        <Checkbox checked={selectedFacilities?.includes(facility.id)} sx={{ '&.Mui-checked': { color: '#008080' } }} />
                                        <ListItemText primary={facility.facility_name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2.5}>
                        <FormControl size='small' fullWidth>
                            <InputLabel id="project-select-label" sx={{ '&.Mui-focused': { color: '#008080' } }}>Project*</InputLabel>
                            <Select
                                labelId="project-select-label"
                                multiple
                                disabled={selectedFacilities?.length === 0}
                                value={selectedProjects}
                                onChange={(e) => handleFilterChange(e, 'project')}
                                input={<OutlinedInput label="Project" />}
                                renderValue={(selected) => selected.length === filteredProjects?.length ? 'All Projects' : selected.length + ' selected'}
                                sx={{
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#008080'
                                    }
                                }}
                                MenuProps={{
                                    autoFocus: false,
                                    disableScrollLock: true,
                                    PaperProps: {
                                        style: {
                                            maxHeight: 350,
                                            overflowY: "auto"
                                        }
                                    }
                                }}
                            >
                                {filteredProjects?.length > 0 && (
                                    <MenuItem value="Select All">
                                        <Checkbox
                                            checked={selectedProjects?.length === filteredProjects?.length}
                                            indeterminate={selectedProjects?.length > 0 && selectedProjects?.length < filteredProjects?.length}
                                            sx={{ '&.Mui-checked': { color: '#008080' }, '&.MuiCheckbox-indeterminate': { color: '#008080' } }}
                                        />
                                        <ListItemText primary="Select All" />
                                    </MenuItem>
                                )}
                                {filteredProjects.map((project) => (
                                    <MenuItem key={project.pid} value={project.pid}>
                                        <Checkbox checked={selectedProjects?.includes(project.pid)} sx={{ '&.Mui-checked': { color: '#008080' } }} />
                                        <ListItemText primary={project.projname.replace("WA-", "").trim()} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {activeTab !== 2 && (
                    <>
                    <Grid item xs={12} md={2.5}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
                            <DatePicker
                                label="From Date"
                                format="DD/MM/YYYY"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small',
                                        sx: {
                                            '& .MuiInputBase-input': {
                                                minWidth: 0 + ' !important'
                                            },
                                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#008080'
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': {
                                                color: '#008080'
                                            }
                                        }
                                    },
                                    day: {
                                        sx: {
                                            '&.Mui-selected': {
                                                backgroundColor: '#008080 !important',
                                                '&:hover': {
                                                    backgroundColor: '#006666 !important'
                                                }
                                            }
                                        }
                                    }
                                }}
                                value={dayjs(fromDate)}
                                maxDate={dayjs(maxDate)}
                                onChange={(newValue) => setFromDate(dayjs(newValue).toDate())}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} md={2.5}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
                            <DatePicker
                                label="To Date"
                                format="DD/MM/YYYY"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small',
                                        sx: {
                                            '& .MuiInputBase-input': {
                                                minWidth: 0 + ' !important'
                                            },
                                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#008080'
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': {
                                                color: '#008080'
                                            }
                                        }
                                    },
                                    day: {
                                        sx: {
                                            '&.Mui-selected': {
                                                backgroundColor: '#008080 !important',
                                                '&:hover': {
                                                    backgroundColor: '#006666 !important'
                                                }
                                            }
                                        }
                                    }
                                }}
                                value={dayjs(toDate)}
                                maxDate={dayjs(maxDate)}
                                minDate={dayjs(fromDate)}
                                onChange={(newValue) => setToDate(dayjs(newValue).toDate())}
                            />
                        </LocalizationProvider>
                    </Grid>
                    </>
                    )}
                    <Grid item xs={12} md={1}>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={handleApplyFilters}
                            style={{ height: '40px', backgroundColor: '#008080' }}
                        >
                            Apply
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleClearFilters}
                            style={{ height: '40px', color: '#008080', borderColor: '#008080' }}
                        >
                            Clear
                        </Button>
                    </Grid>
                </Grid>
            )}
            <Grid container spacing={2} style={{ marginBottom: '24px' }}>
                <Grid item xs={12}>
                    {sankeyLoader ? (
                        <div className='w3-center' style={{ marginTop: 30 }}>
                            <CircularProgress />
                        </div>
                    ) : !hasAppliedFilters ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '60px 40px',
                            color: '#666',
                            backgroundColor: '#f9f9f9',
                            borderRadius: '8px',
                            border: '1px dashed #ddd'
                        }}>
                            <p style={{ fontSize: '16px', margin: 0 }}>Select filters and click Apply</p>
                        </div>
                    ) : sankeyData && sankeyData.nodes && sankeyData.nodes.length > 0 ? (
                        <div style={{ width: '100%' }}>
                            {/* Date filter buttons and legend for live view */}
                            {!showFilters && activeTab !== 2 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                    {/* Chart Legend on the left */}
                                    <div style={{ flex: 1 }}>
                                        <ChartLegend showEquipment={showEquipment} />
                                    </div>

                                    {/* Date buttons and View Insights on the right */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                                        <div>
                                            {DATE_FILTER_OPTIONS.map((option) => (
                                                <button
                                                    key={option.value}
                                                    style={{
                                                        backgroundColor: selectedDateFilter === option.value ? '#008080' : '#6c757d',
                                                        color: '#fff',
                                                        marginRight: 4
                                                    }}
                                                    className="btn btn-secondary btn-sm tabs-nprevious-days"
                                                    onClick={() => handleDateFilterChange(option.value)}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                        {sankeyData && showInsightButton && (
                                            <Button
                                                style={{ borderColor: '#008080', color: '#008080' }}
                                                id="water-balance-sankey-insight"
                                                size='small'
                                                startIcon={<Lightbulb style={{ color: '#008080' }} />}
                                                variant='outlined'
                                                onClick={prepareInsightModal}
                                            >
                                                View Insights
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Chart Legend for filter view */}
                            {showFilters && activeTab !== 2 && (
                                <div style={{ marginBottom: '16px' }}>
                                    <ChartLegend showEquipment={showEquipment} />
                                </div>
                            )}

                            {/* Tabs */}
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '16px' }}>
                                <Tabs
                                    value={activeTab}
                                    onChange={(_, newValue) => setActiveTab(newValue)}
                                    sx={{
                                        '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', fontSize: '14px' },
                                        '& .MuiTab-root.Mui-selected': { color: '#008080' },
                                        '& .MuiTabs-indicator': { backgroundColor: '#008080' }
                                    }}
                                >
                                    <Tab label="Fresh Water Management" />
                                    <Tab label="Recycle Water Management" />
                                    {isAdmin && <Tab label="Water Usage" />}
                                </Tabs>
                            </Box>

                            {/* Tab 0: Fresh Water Management */}
                            {activeTab === 0 && (
                                <div>
                                    <FreshWaterSummaryCards summary={summaryData} />
                                    <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px 16px 0px 0px', backgroundColor: '#fafafa' }}>
                                        <div style={{ 
                                            fontSize: '12px', 
                                            fontWeight: 'bold', 
                                            color: '#006400', 
                                            marginBottom: '4px',
                                            marginLeft: '220px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <div style={{ width: '14px', height: '14px', backgroundColor: 'green', borderRadius: '2px' }} />
                                            Fresh Water
                                        </div>
                                        {freshWaterData && freshWaterData.nodes.length > 0 ? (
                                            <div style={{ height: '280px', width: '100%', position: 'relative', overflow: 'visible' }}>
                                                <ResponsiveSankey
                                                    data={freshWaterData}
                                                    margin={{ top: 20, right: 220, bottom: 10, left: 220 }}
                                                    nodeOpacity={1}
                                                    nodeThickness={15}
                                                    colors={{ datum: "color" }}
                                                    nodeSpacing={40}
                                                    nodeBorderWidth={0}
                                                    linkTooltip={CustomTooltip}
                                                    nodeTooltip={CustomTooltip}
                                                    nodeBorderRadius={4}
                                                    enableLinkGradient={true}
                                                    labelOrientation="horizontal"
                                                    labelPosition='outside'
                                                    labelPadding={10}
                                                    labelTextColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
                                                    label={(node) => {
                                                        const originalLabel = node.label || node.id || '';
                                                        if (!node.data) { node.data = {}; }
                                                        node.data.originalLabel = originalLabel;
                                                        const maxLength = 35;
                                                        if (originalLabel && originalLabel.length > maxLength) {
                                                            return originalLabel.substring(0, maxLength) + '...';
                                                        }
                                                        return originalLabel;
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                                <p>No fresh water data available</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Tab 1: Recycle Water Management */}
                            {activeTab === 1 && (
                                <div>
                                    <RecycleWaterSummaryCards summary={summaryData} oee={oeeData} />

                                    {/* Recycle Water Sankey */}
                                    <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px 16px 0px 0px', backgroundColor: '#fafafa', marginBottom: '16px' }}>
                                        <div style={{ 
                                            fontSize: '12px', 
                                            fontWeight: 'bold', 
                                            color: '#cc8400', 
                                            marginBottom: '4px',
                                            marginLeft: '220px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <div style={{ width: '14px', height: '14px', backgroundColor: '#ffa600', borderRadius: '2px' }} />
                                            Recycle Water
                                        </div>
                                        {recycledWaterData && recycledWaterData.nodes.length > 0 ? (
                                            <div style={{ height: '280px', width: '100%', position: 'relative', overflow: 'visible' }}>
                                                <ResponsiveSankey
                                                    data={recycledWaterData}
                                                    margin={{ top: 20, right: 220, bottom: 10, left: 220 }}
                                                    nodeOpacity={1}
                                                    nodeThickness={15}
                                                    colors={{ datum: "color" }}
                                                    nodeSpacing={40}
                                                    nodeBorderWidth={0}
                                                    linkTooltip={CustomTooltip}
                                                    nodeTooltip={CustomTooltip}
                                                    nodeBorderRadius={4}
                                                    enableLinkGradient={true}
                                                    labelOrientation="horizontal"
                                                    labelPosition='outside'
                                                    labelPadding={10}
                                                    labelTextColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
                                                    label={(node) => {
                                                        const originalLabel = node.label || node.id || '';
                                                        if (!node.data) { node.data = {}; }
                                                        node.data.originalLabel = originalLabel;
                                                        const maxLength = 35;
                                                        if (originalLabel && originalLabel.length > maxLength) {
                                                            return originalLabel.substring(0, maxLength) + '...';
                                                        }
                                                        return originalLabel;
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                                <p>No recycled water data available</p>
                                            </div>
                                        )}

                                        {/* Energy Consumption */}
                                        {energySankeyData && energySankeyData.nodes.length > 0 && (
                                            <div style={{ marginTop: '16px', borderTop: '1px dashed #ccc', paddingTop: '8px' }}>
                                                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#666', marginBottom: '4px', marginLeft: '220px' }}>
                                                    Energy Consumption
                                                </div>
                                                <div style={{ height: '150px', width: '100%', position: 'relative', overflow: 'visible' }}>
                                                    <ResponsiveSankey
                                                        data={energySankeyData}
                                                        margin={{ top: 10, right: 220, bottom: 10, left: 220 }}
                                                        nodeOpacity={1}
                                                        nodeThickness={12}
                                                        colors={{ datum: "color" }}
                                                        nodeSpacing={20}
                                                        nodeBorderWidth={0}
                                                        linkTooltip={CustomTooltip}
                                                        nodeTooltip={CustomTooltip}
                                                        nodeBorderRadius={4}
                                                        enableLinkGradient={true}
                                                        labelOrientation="horizontal"
                                                        labelPosition='outside'
                                                        labelPadding={10}
                                                        labelTextColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
                                                        label={(node) => {
                                                            const originalLabel = node.label || node.id || '';
                                                            if (!node.data) { node.data = {}; }
                                                            node.data.originalLabel = originalLabel;
                                                            const maxLength = 45;
                                                            if (originalLabel && originalLabel.length > maxLength) {
                                                                return originalLabel.substring(0, maxLength) + '...';
                                                            }
                                                            return originalLabel;
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* OCEMS (Quality) */}
                                        {ocemsData && ocemsData.nodes.length > 0 && (
                                            <div style={{ marginTop: '16px', borderTop: '1px dashed #ccc', paddingTop: '8px' }}>
                                                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#666', marginBottom: '4px', marginLeft: '220px' }}>
                                                    OCEMS (Quality)
                                                </div>
                                                <div style={{ height: '150px', width: '100%', position: 'relative', overflow: 'visible' }}>
                                                    <ResponsiveSankey
                                                        data={ocemsData}
                                                        margin={{ top: 10, right: 220, bottom: 10, left: 220 }}
                                                        nodeOpacity={1}
                                                        nodeThickness={12}
                                                        colors={{ datum: "color" }}
                                                        nodeSpacing={20}
                                                        nodeBorderWidth={0}
                                                        linkTooltip={CustomTooltip}
                                                        nodeTooltip={CustomTooltip}
                                                        nodeBorderRadius={4}
                                                        enableLinkGradient={true}
                                                        labelOrientation="horizontal"
                                                        labelPosition='outside'
                                                        labelPadding={10}
                                                        labelTextColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
                                                        label={(node) => {
                                                            const originalLabel = node.label || node.id || '';
                                                            if (!node.data) { node.data = {}; }
                                                            node.data.originalLabel = originalLabel;
                                                            const maxLength = 45;
                                                            if (originalLabel && originalLabel.length > maxLength) {
                                                                return originalLabel.substring(0, maxLength) + '...';
                                                            }
                                                            return originalLabel;
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Equipment Running Hours */}
                                        {showEquipment && equipmentData && equipmentData.nodes.length > 0 && (
                                            <div style={{ marginTop: '16px', borderTop: '1px dashed #ccc', paddingTop: '8px' }}>
                                                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#666', marginBottom: '4px', marginLeft: '220px' }}>
                                                    Equipment Running Hours
                                                </div>
                                                <div style={{ height: Math.max(150, equipmentData.nodes.length * 20) + 'px', width: '100%', position: 'relative', overflow: 'visible' }}>
                                                    <ResponsiveSankey
                                                        data={equipmentData}
                                                        margin={{ top: 10, right: 220, bottom: 10, left: 220 }}
                                                        nodeOpacity={1}
                                                        nodeThickness={12}
                                                        colors={{ datum: "color" }}
                                                        nodeSpacing={20}
                                                        nodeBorderWidth={0}
                                                        linkTooltip={CustomTooltip}
                                                        nodeTooltip={CustomTooltip}
                                                        nodeBorderRadius={4}
                                                        enableLinkGradient={true}
                                                        labelOrientation="horizontal"
                                                        labelPosition='outside'
                                                        labelPadding={10}
                                                        labelTextColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
                                                        label={(node) => {
                                                            const originalLabel = node.label || node.id || '';
                                                            if (!node.data) { node.data = {}; }
                                                            node.data.originalLabel = originalLabel;
                                                            const cleanedLabel = originalLabel.replace(/\s*,?\s*running\s*time\s*,?\s*hrs?\s*/i, '').trim();
                                                            const maxLength = 45;
                                                            if (cleanedLabel && cleanedLabel.length > maxLength) {
                                                                return cleanedLabel.substring(0, maxLength) + '...';
                                                            }
                                                            return cleanedLabel;
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Tab 2: Water Usage — admin/spoc only */}
                            {isAdmin && activeTab === 2 && (
                                <WaterUsageGauges projectIds={projectIds && projectIds.length > 0 ? projectIds : selectedProjects} />
                            )}
                        </div>
                    ) : (
                        <div style={{ width: '100%', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px 16px 0px 0px', backgroundColor: '#fafafa' }}>
                            {!showFilters && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                                    {DATE_FILTER_OPTIONS.map((option) => (
                                        <button
                                            key={option.value}
                                            style={{
                                                backgroundColor: selectedDateFilter === option.value ? '#008080' : '#6c757d',
                                                color: '#fff',
                                                marginRight: 4
                                            }}
                                            className="btn btn-secondary btn-sm tabs-nprevious-days"
                                            onClick={() => handleDateFilterChange(option.value)}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div style={{
                                textAlign: 'center',
                                padding: '60px 40px',
                                color: '#666',
                                backgroundColor: '#f9f9f9',
                                borderRadius: '8px',
                                border: '1px dashed #ddd'
                            }}>
                                <p style={{ fontSize: '16px', margin: 0 }}>Chart data not available</p>
                            </div>
                        </div>
                    )}
                </Grid>
            </Grid>

            {/* Insights Modal */}
            <InsightsModal
                convertedJson={sankeyData}
                openInsight={openInsightModal}
                closeInsight={() => setOpenInsightModal(false)}
                setShowInsightButton={() => setShowInsightButton(true)}
                insightKey={insightKey}
            />
        </div>
    )
}
